import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { withScriptjs, withGoogleMap, GoogleMap, Marker, InfoWindow } from "react-google-maps";
import BootstrapTable from "react-bootstrap-table-next";
import { Card, CardHeader, CardBody } from "reactstrap";
import cellEditFactory, { Type } from "react-bootstrap-table2-editor";
import { useDispatch, useSelector } from "react-redux";
import { Ability } from "@casl/ability";
import { getStatusCodes } from "../../redux/actions/monitoringAction";
import PopUpFiltering from "./PopUpFiltering";
import NetworkInfo from "../Components/Device/NetworkInfo/NetworkInfo";
import AddOrEditServicesOrder from "../orders/routes/services/AddOrEditServicesOrder";
import OpenServiceOrderIndicator from "../Components/orders/Services/OpenServiceOrderIndicator";
import ServiceHistoryModal, { getServiceOrderHistoryColumns } from "../Components/orders/Services/ServiceHistoryModal";
import MonitorFilter from "./MonitorFilter";
import API_ENDPOINTS from "../../configs/API_ENDPOINTS";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import RemotePagination from "./remotePagination";
import BootstrapTablePagination from "../../utils/RemotePagination";
import { textTranslate, translate } from "../../utils/i18n";
import "./moni.css";
import "../Components/Device/DevicePage.css";
import "react-datepicker/dist/react-datepicker.css";

const Monitoring = () => {
  const dispatch = useDispatch();
  /** Query params */
  const [currentQueryParams, setCurrentQueryParams] = useState(`${API_ENDPOINTS.MONITORING}?`);
  const [currentErrorLogQueryParams, setCurrentErrorLogQueryParams] = useState(`${API_ENDPOINTS.MONITORING_LOGS}?`);

  /** Endpoint states */
  const [filterData, setFilterData] = useState([]);

  /** Button states */
  const [activeErrorButtonId, setActiveErrorButtonId] = useState(null);
  const [activeServiceButtonId, setActiveServiceButtonId] = useState(null);
  const [errorButtonColor, setErrorButtonColor] = useState("btn btn-outline-info");
  const [serviceButtonColor, setServiceButtonColor] = useState("btn btn-outline-info");

  // Service states
  const [currentServiceDeviceId, setCurrentServiceDeviceId] = useState();
  const [serviceHistory, setServiceHistory] = useState([]);
  const [showServiceHistory, setShowServiceHistory] = useState(false);
  const [serviceHistoryDevice, setServiceHistoryDevice] = useState(null);

  /** PopUp modal/table states */
  const [errorTable, setErrorTable] = useState(false);
  const [addOrEditServiceOrderModal, setAddOrEditServiceOrderModal] = useState(false);
  const [popUpTableData, setPopUpTableData] = useState();
  const [popUpTableId, setPopUpTableId] = useState();
  const [map, setMap] = useState(false);

  /** Pagination states */
  const [sizePerPage, setSizePerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState();

  /** Pagination states ERRORTABLE */
  const [errorTableSizePerPage, setErrorTableSizePerPage] = useState(10);
  const [errorTableCurrentPage, setErrorTableCurrentPage] = useState(1);
  const [errorTableTotalCount, setErrorTableTotalCount] = useState();

  const userAbility = useSelector((state) => state.usersReducer.ability);
  const canManageServiceOrders = new Ability(userAbility || []).can("manage", "serviceOrders");

  const statusCodes = useSelector(
    (state) => state.monitoringsReducer.statusCodes.map((s) => ({ value: s._id, label: s.name, _id: s._id })) || []
  );

  // Getting data on initial render
  useEffect(() => {
    const getData = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.MONITORING}`);
        setSizePerPage(response.data.perPage);
        setCurrentPage(response.data.page);
        setTotalCount(response.data.total);
        setFilterData(response.data.data);
      } catch (error) {
        console.log("Error:" + error);
      }
    };

    getData();
    dispatch(getStatusCodes());
  }, []);

  useEffect(() => {
    if (!addOrEditServiceOrderModal) {
      setShowServiceHistory(false);
    }
  }, [addOrEditServiceOrderModal]);

  useEffect(() => {
    const getPageChangeData = async () => {
      try {
        const response = await axios.get(`${currentQueryParams}&page=${currentPage}`);
        setSizePerPage(response.data.perPage);
        setCurrentPage(response.data.page);
        setTotalCount(response.data.total);
        setFilterData(response.data.data);
      } catch (error) {
        console.log("error");
      }
    };
    getPageChangeData();
  }, [currentPage]);

  useEffect(() => {
    const getMonitoringLogs = async () => {
      if (popUpTableId === undefined) return;
      try {
        const response = await axios.get(
          `${currentErrorLogQueryParams.replace("{monitoringId}", popUpTableId)}page=${errorTableCurrentPage}`
        );
        setPopUpTableData(response.data.data);
        setErrorTableSizePerPage(response.data.perPage);
        setErrorTableTotalCount(response.data.total);
      } catch (error) {
        console.log("error", error);
      }
    };
    getMonitoringLogs();
  }, [errorTableCurrentPage]);

  const onFilteredDevicesPopUpTable = async (start, end) => {
    let url = `${API_ENDPOINTS.MONITORING_LOGS.replace("{monitoringId}", popUpTableId)}?`;

    let dateStart = moment(start);
    let dateEnd = moment(end);
    let formattedStartDate = dateStart.toISOString();
    let formattedEndDate = dateEnd.toISOString();

    if (!start && !end) {
      setCurrentErrorLogQueryParams(`${API_ENDPOINTS.MONITORING_LOGS}`);
    }

    if (start) {
      url += `fromUtc=${formattedStartDate}&`;
    }

    if (end) {
      url += `toUtc=${formattedEndDate}&`;
    }

    setCurrentErrorLogQueryParams(url);
    axios
      .get(url)
      .then(function (response) {
        setPopUpTableData(response.data.data);
        setErrorTableCurrentPage(response.data.page);
        setErrorTableSizePerPage(response.data.perPage);
        setErrorTableTotalCount(response.data.total);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const onFilteredDevices = async (queryParams) => {
    const queryPayload = {
      deviceTypeIds: queryParams?.deviceTypeIds ?? [],
      orgIds: queryParams?.orgIds ?? [],
      deviceModelIds: queryParams?.deviceModelIds ?? [],
      packageIds: queryParams?.packageIds ?? [],
      errorCodes: queryParams?.errorCodes ?? [],
      errorStartDate: queryParams?.errorStartDate ?? "",
      errorEndDate: queryParams?.errorEndDate ?? "",
      lastSeenStartDate: queryParams?.lastSeenStartDate ?? "",
      lastSeenEndDate: queryParams?.lastSeenEndDate ?? "",
      statusIds: queryParams?.statusIds ?? "",
      devices: queryParams?.devices ?? [],
    };
    let url = `${API_ENDPOINTS.MONITORING}?`;
    if (queryPayload.deviceTypeIds) {
      url += `deviceTypeIds=${queryPayload.deviceTypeIds}&`;
    }

    if (queryPayload.orgIds) {
      url += `orgIds=${queryPayload.orgIds}&`;
    }

    if (queryPayload.deviceModelIds) {
      url += `deviceModelIds=${queryPayload.deviceModelIds}&`;
    }

    if (queryPayload.packageIds) {
      url += `packageIds=${queryPayload.packageIds}&`;
    }

    if (queryPayload.errorCodes) {
      url += `errorCodes=${queryPayload.errorCodes}&`;
    }

    if (queryPayload.errorStartDate) {
      url += `errorStartDate=${queryPayload.errorStartDate}&`;
    }

    if (queryPayload.errorEndDate) {
      url += `errorEndDate=${queryPayload.errorEndDate}&`;
    }

    if (queryPayload.lastSeenStartDate) {
      url += `lastSeenStartDate=${queryPayload.lastSeenStartDate}&`;
    }

    if (queryPayload.lastSeenEndDate) {
      url += `lastSeenEndDate=${queryPayload.lastSeenEndDate}&`;
    }

    if (queryPayload.statusIds) {
      url += `status=${queryPayload.statusIds}&`;
    }

    if (queryPayload.devices) {
      url += `devices=${queryPayload.devices}&`;
    }

    url = url.endsWith("&") ? url.slice(0, -1) : url;

    setCurrentQueryParams(url);
    axios
      .get(`${url}&perPage=${sizePerPage}&page=${currentPage}`)
      .then(function (response) {
        setFilterData(response.data.data);
        setTotalCount(response.data.total);
        setSizePerPage(response.data.perPage);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const idLink = (cell, row) => {
    return (
      <Link target="_blank" to={`/devices/${row.device._id}`}>
        {row.device.device}
      </Link>
    );
  };

  const formattedNetworkInfo = (cell, row) => {
    if(!row.device || !row.device.device_model) return <></>;
    return (
      <div className="justify-content-center align-items-center d-flex">
        <span>
          <NetworkInfo isTableMode device={row.device} />
        </span>
      </div>
    );
  };

  const formatedTimestamp = (cell, row) => {
    if (typeof cell === "number") return moment.unix(cell).format("YYYY-MM-DD HH:mm:ss");
    return moment(cell).format("YYYY-MM-DD HH:mm:ss");
  };

  const changePremium = (cell, row) => {
    if (cell?.name === "premium – automatic replacement") {
      cell = "Premium";
      return cell;
    } else return row.package?.name;
  };

  /** POP UP TABLE  */
  const handleErrors = (cell, row) => {
    const buttonClass = activeErrorButtonId === row._id ? errorButtonColor : "btn btn-outline-info text-left";

    return (
      <button
        className={buttonClass}
        onClick={() => {
          setActiveErrorButtonId(row._id);
          showErrorTable(cell, row);
        }}
      >
        {cell.length > 0 ? (
          <>
            {cell.map((item, index) => (
              <span key={index}>
                {item.name === "event3" ? textTranslate("event3") : item.name}
                {item.count ? ` (${item.count})` : ""}
                <br></br>
              </span>
            ))}
          </>
        ) : (
          "N/A"
        )}
      </button>
    );
  };

  const showErrorTable = async (cell, row) => {
    setErrorTable(true);
    setPopUpTableId(row._id);
    try {
      const response = await axios.get(`${API_ENDPOINTS.MONITORING_LOGS.replace("{monitoringId}", row._id)}`);
      setPopUpTableData(response.data.data);
      setErrorTableTotalCount(response.data.total);
      setErrorTableCurrentPage(response.data.page);
      setErrorTableSizePerPage(response.data.perPage);
    } catch (error) {
      console.log(error);
    }
  };

  const changeErrorColor = () => {
    setErrorButtonColor("btn btn-outline-warning");
    setTimeout(() => {
      setErrorButtonColor("btn btn-outline-info");
      setActiveErrorButtonId(null);
    }, 2000);
  };

  const startServiceLink = (cell, row) => {
    const buttonClass = activeServiceButtonId === row._id ? serviceButtonColor : "btn btn-outline-info";

    return (
      <div className="d-flex align-items-center">
        <button
          className={buttonClass}
          title={textTranslate("startService")}
          aria-label={textTranslate("startService")}
          onClick={() => {
            onServiceClick(cell, row);
            setActiveServiceButtonId(row._id);
          }}
        >
          <svg width={20} height={20} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
            <path
              fill="#00497a"
              d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"
            />
          </svg>
        </button>
        <OpenServiceOrderIndicator
          serviceOrders={row.serviceOrders ?? row.device?.serviceOrders}
          onClick={() => setServiceHistoryDevice(row.device)}
        />
      </div>
    );
  };

  const onServiceClick = async (cell, row) => {
    if (addOrEditServiceOrderModal === true) {
      setAddOrEditServiceOrderModal(!addOrEditServiceOrderModal);
      setCurrentServiceDeviceId();
      changeServiceColor();
      setShowServiceHistory((prev) => !prev);
    } else if (!addOrEditServiceOrderModal === true) {
      setAddOrEditServiceOrderModal(!addOrEditServiceOrderModal);
      setCurrentServiceDeviceId(row.device);
      setServiceHistory([]);

      try {
        const response = await axios.get(`${API_ENDPOINTS.SERVICE_ORDERS_DEVICE}`.replace("id", row.device._id));
        setServiceHistory(response.data);
      } catch (error) {}
    }
  };

  const changeServiceColor = () => {
    setServiceButtonColor("btn btn-outline-warning");
    setTimeout(() => {
      setServiceButtonColor("btn btn-outline-info");
      setActiveServiceButtonId(null);
    }, 2000);
  };

  const patchRequest = async (dataField, newValue, rowId, data) => {
    let status = "";
    let notes = "";
    if (dataField === "notes") {
      notes = newValue.trim();
    } else {
      status = newValue.trim();
    }

    try {
      const response = await axios.patch(`${API_ENDPOINTS.MONITORING_LOGS_ID_PATCH.replace("{monitoringId}", rowId)}`, {
        status: status,
        notes: notes, // no trailing spaces
      });
      if (response.status === 200) {
        // Call the callback with new data to update the state
        callback(dataField, newValue, rowId);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const deviceModelAndDeviceName = (cell, row) => {
    return (
      <div>
        <span>{row.device.device_model.deviceTypeCategory.name}</span>
        <br></br>
        <span>{row.device.device_model.title}</span>
      </div>
    );
  };

  const loadPageData = async (type, filters, row, cell) => {
    const { page, sizePerPage, searchText, cellEdit, data } = filters;

    if (type === "cellEdit") {
      const { dataField, newValue, rowId } = cellEdit;

      const result = data.map((row) => {
        if (row._id === rowId) {
          const newRow = { ...row };
          newRow[dataField] = newValue;
          return newRow;
        }
        return row;
      });

      setFilterData(result); // Update state with new data
      patchRequest(dataField, newValue, rowId);
      return;
    }

    let url;

    if (API_ENDPOINTS.DEVICES === currentQueryParams) {
      url = `${API_ENDPOINTS.MONITORING}?`;
    } else {
      url = `${currentQueryParams}&`;
    }

    try {
      const response = await axios.get(`${url}page=${page}&perPage=${sizePerPage}`);
      setFilterData(response.data.data);
      setTotalCount(response.data.total);
      setSizePerPage(response.data.perPage);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  const columns = [
    {
      dataField: "_id",
      text: `ID`,
      editable: false,
      hidden: true,
    },
    {
      dataField: "org.name",
      text: `${textTranslate("owner")}`,
      editable: false,
      hidden: true,
    },
    {
      dataField: "device.device",
      text: `${textTranslate("networkId")}`,
      formatter: idLink,
      editable: false,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      },
    },

    {
      dataField: "device.name",
      text: `${textTranslate("table.deviceName")}`,
      editable: false,
    },
    {
      dataField: "device.lastSeenAt",
      text: `${textTranslate("lastSeenAt")}`,
      editable: false,
      formatter: formatedTimestamp,
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      },
    },
    {
      dataField: "device.device_model.deviceTypeCategory.name",
      text: `${textTranslate("dashboard.deviceType")}`,
      formatter: deviceModelAndDeviceName,
      editable: false,
    },
    {
      dataField: "package",
      text: `${textTranslate("table.package")}`,
      formatter: changePremium,

      editable: false,

      headerStyle: (colum, colIndex) => {
        return { width: "90px" };
      },
    },
    {
      dataField: "latestMessage",
      text: `${textTranslate("networkStrength")}`,
      formatter: formattedNetworkInfo,
      editable: false,
      headerStyle: (colum, colIndex) => {
        return { width: "110px" };
      },
    },
    {
      dataField: "status._id",
      text: `Status`,
      editable: true,
      editor: {
        type: Type.SELECT,
        options: statusCodes,
      },
      headerStyle: (colum, colIndex) => {
        return { width: "120px" };
      },
      formatter: (cell, row) => {
        const statusCode = statusCodes.find((s) => s.value === cell);
        return <span className="text-capitalize">{statusCode.label}</span>;
      },
    },
    {
      dataField: "errorCodes",
      text: `${textTranslate("errorCode")}`,
      editable: false,
      formatter: handleErrors,
      headerStyle: (colum, colIndex) => {
        return { width: "150px" };
      },
    },
    {
      dataField: "latestErrorOccurredAt",
      text: `${textTranslate("lastErrorOcurredAt")}`,
      sort: true,
      sortOrder: "desc",
      formatter: formatedTimestamp,
      editable: false,
      headerStyle: (colum, colIndex) => {
        return { width: "125px" };
      },
    },
    {
      dataField: "notes",
      text: `${textTranslate("notes")}`,
      style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
    },
    {
      dataField: "device.properties.firmwareVersion",
      text: `${textTranslate("firmwareVersion")}`,
      hidden: true,
      editable: false,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      },
    },
    ...(canManageServiceOrders
      ? [
          {
            text: `${textTranslate("serviceOrder")}`,
            formatter: startServiceLink,
            editable: false,
            headerStyle: (colum, colIndex) => {
              return { width: "100px" };
            },
          },
        ]
      : []),
  ];

  const ErrorTableColumn = [
    {
      dataField: "_id",
      text: `ID`,
      editable: false,
    },

    {
      dataField: "owner",
      text: `Owner`,
      editable: false,
    },
    {
      dataField: "deviceModel",
      text: `Device model`,
      editable: false,
    },
    {
      dataField: "errorCode",
      text: `Error`,
      editable: false,
    },

    {
      dataField: "networkType",
      text: `Network Type`,
      editable: false,
    },
    {
      dataField: "occurredAt",
      text: `Occured At`,
      editable: false,
      formatter: formatedTimestamp,
    },
  ];

  const serviceOrderColumn = getServiceOrderHistoryColumns();

  return (
    <div className="font-size-07">
      <Card>
        <CardHeader>
          <i className="icon-chart"></i>
          {textTranslate("monitoringPageTitle")}
        </CardHeader>
        <CardBody>
          <div className="row mb-2">
            <div className="col-12 d-flex justify-content-end">
              <button type="button" className="btn btn-success text-dark" onClick={() => setMap((prev) => !prev)}>
                {textTranslate("map")}
                <i className="ml-2 fa fa-globe" aria-hidden="true"></i>
              </button>
              {/* <button type="button" className="btn btn-success text-dark mb-4">
                {textTranslate("mode.table")}
                <i className="ml-2 fa fa-table" aria-hidden="true"></i>
              </button> */}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <MonitorFilter data={filterData} onFilteredDevices={onFilteredDevices}></MonitorFilter>
            </div>
          </div>

          <>
            <BootstrapTablePagination
              data={filterData || []}
              page={currentPage}
              columns={columns}
              sizePerPage={sizePerPage}
              totalSize={totalCount}
              onTableChange={loadPageData}
              enableSearch={false}
              cellEdit={cellEditFactory({ mode: "click", blurToSave: true })}
            ></BootstrapTablePagination>
          </>
          {errorTable && popUpTableData && (
            <div className="modal-overlay ">
              <div className="p-2">
                <div className="my-4">
                  <PopUpFiltering
                    onFilteredDevicesPopUpTable={onFilteredDevicesPopUpTable}
                    data={popUpTableData}
                  ></PopUpFiltering>
                </div>
                <div className="my-4 mx-3">
                  <BootstrapTable keyField="_id" hover striped data={popUpTableData} columns={ErrorTableColumn}></BootstrapTable>
                </div>
                <RemotePagination
                  className="pagination-bar"
                  currentPage={errorTableCurrentPage}
                  totalCount={errorTableTotalCount}
                  pageSize={errorTableSizePerPage}
                  onPageChange={(page) => {
                    setErrorTableCurrentPage(page);
                  }}
                  onFilteredDevices={onFilteredDevicesPopUpTable}
                ></RemotePagination>
                <button
                  className="btn btn-danger float-right mx-3"
                  onClick={() => {
                    setErrorTable(false);
                    changeErrorColor();
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </CardBody>

        {addOrEditServiceOrderModal && (
          <>
            <div className="modal-overlay px-4">
              <div className="modal-content">
                <AddOrEditServicesOrder
                  onServiceClick={onServiceClick}
                  deviceId={currentServiceDeviceId}
                ></AddOrEditServicesOrder>

                {serviceHistory && serviceHistory.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-info float-left my-3"
                    onClick={() => setShowServiceHistory((prev) => !prev)}
                  >
                    {textTranslate("showServiceHistory")}
                  </button>
                ) : (
                  <></>
                )}

                {showServiceHistory && serviceHistory && serviceHistory.length > 0 && (
                  <>
                    <ToolkitProvider keyField="_id" data={serviceHistory} columns={serviceOrderColumn}>
                      {(props) => (
                        <>
                          <div className="group float-right mt-3 mr-4"></div>

                          <BootstrapTable key={Math.random()} {...props.baseProps} striped hover />
                        </>
                      )}
                    </ToolkitProvider>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        <ServiceHistoryModal device={serviceHistoryDevice} onClose={() => setServiceHistoryDevice(null)} />

        {map && (
          <MyMapComponent
            data={filterData}
            isMarkerShown
            googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyChLzNxCb4QKr0o3lYn1L7h_q5ZMt8u_Q4&v=3.exp"
            loadingElement={<div style={{ height: `100%` }} />}
            containerElement={<div style={{ height: `600px` }} />}
            mapElement={<div style={{ height: `100%` }} />}
          />
        )}
      </Card>
    </div>
  );
};

const MyMapComponent = withScriptjs(
  withGoogleMap((props) => (
    <GoogleMap
      defaultZoom={7}
      defaultCenter={{
        lat: 55.836666,
        lng: 10.598629,
      }}
    >
      {props.data &&
        props.data.map(
          (
            device // limit on 100
          ) => <MapMarker key={device.device._id} device={device.device}></MapMarker>
        )}
    </GoogleMap>
  ))
);

const MapMarker = ({ device, ...props }) => {
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [collapse, setCollapse] = useState(false);

  return (
    <Marker
      key={"Marker-" + device._id}
      position={{ lat: device.geo.lat, lng: device.geo.lon }}
      title={device.device_type}
      icon={{
        path: "M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z",
        fillColor: "#F3A35D",
        fillOpacity: 0.8,
        scale: 0.5,
        strokeColor: "#fff",
        strokeWeight: 1,
      }}
      onClick={() => {
        setShowInfoWindow((prev) => !prev);
      }}
    >
      {showInfoWindow && (
        <InfoWindow
          position={{
            lat: 55.836666,
            lng: 10.598629,
          }}
        >
          <div className="cardB">
            <b>
              <h6> {device.name}</h6>
            </b>
            <ul className="list-group list-group-flush bg.info">
              {device.latestMessage &&
                typeof device.latestMessage.data !== "undefined" &&
                device.device_model.data_model.map((item, index) => {
                  const latestMessageData = device.latestMessage.data[index];

                  return (
                    <>
                      <div className=" my-1 d-flex justify-content-between d-inline p-2 bg-secondary  rounded">
                        <span className="p-2  text-dark font-weight-bold">{translate(item?.unit?.slug)} </span>
                        <span className="  ml-3 text-dark  font-weight-bold">
                          {latestMessageData?.v ?? translate("soc_missing")} {item?.unit?.value}
                        </span>
                      </div>
                    </>
                  );
                })}
              <p className="mx-auto"> {moment(device.latestMessage.time).locale("da").format("YYYY-MM-DD HH:mm:ss")}</p>
              <Link className=" my-2 btn btn-primary" target="_blank" to={`/devices/${device._id}`}>
                {translate("goToDevice")}
              </Link>
            </ul>
          </div>
        </InfoWindow>
      )}
    </Marker>
  );
};

export default Monitoring;
